import { Form, Modal, Typography } from "antd"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography

interface RenameFileModalProps {
  fileName: string
  onClose: () => void
  onSubmit: (nextFileName: string) => Promise<void>
}

function splitFileName(fileName: string) {
  const dot = fileName.lastIndexOf(".")
  if (dot <= 0) return { stem: fileName, ext: "" }
  return { stem: fileName.slice(0, dot), ext: fileName.slice(dot) }
}

export function RenameFileModal({
  fileName,
  onClose,
  onSubmit
}: RenameFileModalProps) {
  const { t } = useTranslation()
  const { stem, ext } = useMemo(() => splitFileName(fileName), [fileName])
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm<{ stem: string }>()

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const next = `${values.stem.trim()}${ext}`
      if (next === fileName) {
        onClose()
        return
      }

      setIsLoading(true)
      await onSubmit(next)
      onClose()
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      title={t("projectFiles.rename.title")}
      open
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("projectFiles.rename.save")}
      cancelText={t("projectFiles.rename.cancel")}
      confirmLoading={isLoading}
    >
      <Form form={form} layout="vertical" initialValues={{ stem }}>
        <Form.Item
          label={t("projectFiles.rename.nameLabel")}
          name="stem"
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("projectFiles.rename.nameRequired")
            }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            maxLength={200}
            suffix={ext ? <Text type="secondary">{ext}</Text> : null}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
